import { $lang, getTagFromNode, useMeasurementUnitsList, useTranslation } from '@msdk/common'
import {
  ControlValidationType,
  FormFieldDefinition,
  FormOpenMode,
  getSafeValueLimits,
  getSeparator,
  NumericFieldDefinition,
  NumericValue,
  tryConvert,
  tryConvertDecimalInfo,
  tryGetConversionUM,
} from '@msdk/definitions'
import {
  createComponentWrapperWithRef,
  FormField,
  FormFieldLabel,
  InputAdornment,
  MUI_PALETTE,
  TextInput,
  Typography,
  uiTokens,
} from '@msdk/ui'
import React from 'react'
import { NumberFormatValues, NumericFormat, SourceInfo } from 'react-number-format'
import { useParams } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import { useFieldLabel, useFieldSeverity, useFieldValue, useHasMandatoryValidation } from '../../../hooks'
import { FieldProps } from '../../../models/form'
import { getTranslatedMu, getValidationMessagesWithRangeHelperText, nodeHasFlag } from '../../../utils'
import { ValidationMessages } from '../../ValidationMessages'

// max allowed number the user can input, this is here
// to prevent overflows with conversion or formatting calculations
// but the user realistically should never input a number this high inside
// a number field, we would incur in other precision issues anyway.
const MAX_ALLOWED_NUMBER = 899999999999999
const MAX_DIGITS_ALLOWED = 15

const Unit = createComponentWrapperWithRef(
  {
    mui: Typography,
    nova: Typography,
    muiProps: {
      variant: 'body2',
      color: MUI_PALETTE.text.secondary,
    },
    novaProps: {
      variant: 'body2',
      color: uiTokens.uiContent.neutral,
    },
  },
  'Status',
)

export function NumericField({ node, onChange, fieldArgs }: FieldProps) {
  const fieldDef = node.value.fieldDefinition as NumericFieldDefinition
  const formDef = node.value.formDefinition as FormFieldDefinition
  const fieldLabel = useFieldLabel(fieldDef, formDef)
  const [value, setValue] = useFieldValue(node, onChange, 10)
  const lang = useRecoilValue($lang)
  const t = useTranslation()
  const { moduleName } = useParams()
  const mus = useMeasurementUnitsList() ?? []

  const hasMandatoryFlag = nodeHasFlag(node, ControlValidationType.Mandatory)
  const hasMandatory = useHasMandatoryValidation(node, fieldDef.entityName)

  const onFieldChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
    const newValue = values.floatValue
    if (sourceInfo.event) {
      const newValueDefined = newValue !== null && newValue !== undefined
      const muKey = tryGetConversionUM(fieldDef.um, fieldDef.entityName, fieldDef.name)
      const fieldValue = newValueDefined ? new NumericValue(newValue, muKey) : null
      node.value.isDirty = true
      setValue(fieldValue)
    }
  }

  const getDecimalInfo = () => {
    return tryConvertDecimalInfo(fieldDef.decimalInfo, fieldDef.um, fieldDef.entityName, fieldDef.name, fieldDef.step)
  }

  const safeValueLimits =
    fieldDef.safeValueLimits ?? getSafeValueLimits(fieldDef.dbType, fieldDef.precision, fieldDef.scale)

  const maxAllowedNumber = Math.min(
    MAX_ALLOWED_NUMBER,
    tryConvert(safeValueLimits.max, fieldDef.um, fieldDef.entityName, fieldDef.name),
  )
  const minAllowerNumber = Math.max(
    -MAX_ALLOWED_NUMBER,
    tryConvert(safeValueLimits.min, fieldDef.um, fieldDef.entityName, fieldDef.name),
  )

  const decimalSeparator = getSeparator(lang, 'decimal', value ? (value.value as number) : undefined)
  const thousandSeparator = getSeparator(lang, 'group', value ? (value.value as number) : undefined)

  const validateInput = (values: NumberFormatValues) => {
    const totalDigits = values.value.replaceAll(decimalSeparator, '').replaceAll(thousandSeparator, '').length

    let isWithingFormattedMaxDigits = true
    if (totalDigits > MAX_DIGITS_ALLOWED) {
      isWithingFormattedMaxDigits = false
    } else if (totalDigits === MAX_DIGITS_ALLOWED && values.value.endsWith(decimalSeparator)) {
      isWithingFormattedMaxDigits = false
    }

    return (
      (!values.floatValue || (values.floatValue <= maxAllowedNumber && values.floatValue >= minAllowerNumber)) &&
      isWithingFormattedMaxDigits
    )
  }

  const isReadOnly = node.value.state === 'Readonly'
  const severity = useFieldSeverity(fieldDef, node)
  const validationMessages = getValidationMessagesWithRangeHelperText(node.value.validationMessages, node.value.range)

  const muKey = tryGetConversionUM(fieldDef.um, fieldDef.entityName, fieldDef.name)
  const um = getTranslatedMu(muKey, mus)

  return (
    <FormField
      required={(hasMandatory || hasMandatoryFlag) && fieldArgs?.formOpenMode !== FormOpenMode.Readonly}
      size="small"
      data-test-id={getTagFromNode(fieldDef, moduleName)}
      severity={severity}
      readOnly={isReadOnly}
      disabled={isReadOnly}
    >
      {fieldArgs?.showLabel && <FormFieldLabel>{fieldLabel}</FormFieldLabel>}
      <NumericFormat
        label={fieldArgs?.showLabel ? fieldLabel : undefined}
        value={value ? (value.value as number) : ''}
        allowLeadingZeros
        allowNegative
        customInput={TextInput}
        decimalScale={getDecimalInfo()?.decimalLimit}
        decimalSeparator={decimalSeparator}
        thousandSeparator={thousandSeparator}
        onValueChange={onFieldChange}
        disabled={isReadOnly}
        placeholder={fieldArgs?.showLabel ? t('CMPS_TYPE_HERE_PLACEHOLDER', 'Type here') : undefined}
        isAllowed={validateInput}
        endAdornment={
          um ? (
            <InputAdornment position="end">
              <Unit>{um}</Unit>
            </InputAdornment>
          ) : undefined
        }
      />
      <ValidationMessages messages={validationMessages} fieldDef={fieldDef} node={node} />
    </FormField>
  )
}
